import {useContext, useEffect, useRef, useState, useCallback, useMemo} from "react";
import {gsap} from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import mush from "../assets/projectLogos/mush.png";
import cashflow from "../assets/projectLogos/cashflow.png";
import mernSticker from "../assets/stickers/mernSticker.svg";
import javaSticker from "../assets/stickers/javaSticker.svg";
import gMail from "../assets/socials/gMail.svg";
import insta from "../assets/socials/insta.svg";
import linkedIn from "../assets/socials/linkedIn.svg";
import github from "../assets/socials/github.svg";
import "../utils.css";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ClickContext from "../context/ClickContext";
import Modal from "./Modal";

gsap.registerPlugin(ScrollTrigger);

const Projects = () => {
  const containerRef = useRef(null);
  const {projectClick, projectRef} = useContext(ClickContext);

  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());

  const projectModalOverlayRef = useRef(null);
  const projectModalContentRef = useRef(null);
  const clickedProjectRef = useRef(null);

  // Memoized slider settings
  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: false,
      speed: 500,
      slidesToShow: 3,
      slidesToScroll: 3,
      initialSlide: 0,
      prevArrow: null,
      nextArrow: null,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2,
            infinite: true,
            dots: true,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: true,
            dots: true,
          },
        },
      ],
    }),
    []
  );

  // Memoized projects data
  const projects = useMemo(
    () => [
      {
        id: 1,
        name: "Mush Social",
        img: mush,
        bgColor: "green",
        technologySticker: mernSticker,
        description: "Developed and optimized features for a social media app, ensuring clean, efficient code using Express, Sequelize with MySQL, and Socket.io. Integrated YouTube Data API for music, Gemini API for generative AI and Google Nearby API for location-based services. Implemented image optimization with Sharp and AWS S3 for file uploads. Added push notifications for activity updates. Refined and optimized the posts API to address performance bottlenecks as the project scaled. With the addition of multiple modules to the posts functionality, the API experienced high latency. I improved the code by fetching only the necessary attributes, reducing unnecessary data retrieval and improving overall response times, scalability, and readability.",
        technologies: "Node, Express, JWT, Passport, Sequelize, Multer, Yup, Morgan",
        features: {
          "Post Recommendation": "Personalized content based on user interactions, boosting engagement and retention.",
          "AI Integration": "Integrated Gemini API into a social media app to enable AI-driven chat, text generation for posts and comments, and image creation. Implemented secure user data retrieval to personalize content without exposing sensitive information.",
          "Location-Based Posts": "Integrated Google Nearby API for real-time location sharing and location-based rewards.",
          "Subscription Features": "Enabled premium features via virtual points earned through app interactions.",
          "Privately Shared Posts": "Developed private sharing for user-controlled content visibility, ensuring privacy and security.",
        },
        liveUrl: "",
        githubUrl: "",
      },
      {
        id: 2,
        name: "Cashflow",
        img: cashflow,
        bgColor: "cyan",
        technologySticker: mernSticker,
        description: "From scratch, I created an accounting and invoicing application using the TypeScript + Express. User authentication, invoice creation, expense tracking, and expense reporting were included as core functionalities. Business logic and data access layers were incorporated with Sequelize ORM for optimal database access and data management. With class-validator and routing-controllers, declarative request validation was applied increasing code maintainability and clarity. Backend practices concerning scalability, modular design, and optimized application performance were integrated into the architecture throughout development.",
        technologies: "Typedi, Routing Controllers, Class transformer, Class validator, Passport, JWT, Swagger UI, Winston, Morgan, Sequelize Typescript",
        features: {
          "Bank Statement/Journal Entries Import": "By importing files like csv/xlsx type first headers of the file are extracted and sent to frontend to map the columns with the required fields to created journal entries after mapping validation is done for e.g. datatypes are checked proper difference of an entry is checked and then all records are stored in the database",
          "Account Management": "Vendors accounts, Customer accounts, Bank accounts are managed so that cashflow, revenue etc. of the business can be calculated properly.",
          Dashboard: "Realtime data of the revenue, income, expense, cashflow, sales and purchase are shown so that user can determine their business growth.",
          "Contacts Management": "Contacts like vendors and customer are managed separately so that user can perform various action like invoice generation, money management, etc. can be done",
        },
        liveUrl: "",
        githubUrl: "",
      },
      {
        id: 3,
        name: "Dex Status",
        img: null, // No image available
        bgColor: "orange",
        technologySticker: javaSticker,
        description: "Developed and optimized a Node.js application for decentralized exchange (DEX) token tracking and promotion, ensuring clean, efficient code using Node.js, Express, Routing Controllers, Class Validator, Typedi, and various third-party APIs. Integrated DEX APIs for real-time token data retrieval and implemented Solana-based payment functionality to allow users to promote tokens for a specific period. Built a Telegram bot to scrape messages from group chats, extract relevant data, and automatically post it to both the Telegram group and Twitter, enabling seamless cross-platform interaction. Optimized the data flow by fetching only necessary attributes, reducing the load on the system and improving scalability. Enhanced the user experience by adding trending token functionality and payment options for token promotion.",
        technologies: "Typedi, Routing Controllers, Class transformer, Class validator, Passport, JWT, Swagger UI, Winston, Morgan, Sequelize Typescript",
        features: {
          "Token Tracking": "Integrated DEX APIs for live tracking of tokens and their latest market data.",
          "Token Promotion": "Developed a Solana-based payment system allowing users to promote tokens, making them trend for a set duration.",
          "Telegram Bot Integration": "Automated scraping of messages from Telegram group chats, with data extraction and posting to Telegram and Twitter.",
          "Real-Time Data": "Ensured real-time updates on tokens and trends, providing users with the latest information instantly.",
          "Cross-Platform Integration": "Enabled seamless posting to both Telegram and Twitter, boosting token visibility across platforms.",
        },
        liveUrl: "",
        githubUrl: "",
      },
    ],
    []
  );

  // Handle image load errors
  const handleImageError = useCallback((projectId) => {
    setImageErrors((prev) => new Set([...prev, projectId]));
  }, []);

  // GSAP animation effect
  useEffect(() => {
    if (!containerRef.current) return;

    const animation = gsap.fromTo(
      containerRef.current.children,
      {x: "100%"},
      {
        x: "0%",
        stagger: 0.2,
        duration: 1,
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 90%",
          end: "bottom 85%",
          scrub: true,
        },
      }
    );

    return () => {
      animation.kill();
    };
  }, []);

  // Modal animation functions
  const openProjectModal = useCallback((project, cardElement) => {
    setSelectedProject(project);
    clickedProjectRef.current = cardElement;

    const modalOverlay = projectModalOverlayRef.current;
    const modalContent = projectModalContentRef.current;

    if (!modalOverlay || !modalContent) return;

    const rect = cardElement.getBoundingClientRect();

    gsap.set(modalOverlay, {
      display: "block",
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      opacity: 1,
      borderRadius: "50px",
      boxShadow: "5px 5px 0px 5px black",
    });

    gsap.set(modalContent, {
      opacity: 0,
      height: 0,
      width: 0,
    });

    gsap.to(modalOverlay, {
      left: "50%",
      top: "50%",
      xPercent: -50,
      yPercent: -50,
      width: "90vw",
      height: "90vh",
      borderRadius: "20px",
      duration: 0.5,
      ease: "power2.inOut",
      onComplete: () => {
        gsap.to(modalContent, {
          opacity: 1,
          duration: 0.3,
          height: "100%",
          width: "100%",
        });
      },
    });

    setIsProjectModalOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeProjectModal = useCallback(() => {
    const cardElement = clickedProjectRef.current;
    const modalOverlay = projectModalOverlayRef.current;
    const modalContent = projectModalContentRef.current;

    if (!cardElement || !modalOverlay || !modalContent) return;

    const rect = cardElement.getBoundingClientRect();

    gsap.to(modalContent, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        gsap.to(modalOverlay, {
          left: rect.left,
          top: rect.top,
          xPercent: 0,
          yPercent: 0,
          width: rect.width,
          height: rect.height,
          borderRadius: "50px",
          duration: 0.5,
          ease: "power2.inOut",
          onComplete: () => {
            gsap.set(modalOverlay, {display: "none"});
            setIsProjectModalOpen(false);
            setSelectedProject(null);
          },
        });
      },
    });

    document.body.style.overflow = "auto";
  }, []);

  // Component for project image or fallback
  const ProjectImage = ({project, isModal = false}) => {
    const hasImageError = imageErrors.has(project.id);
    const shouldShowFallback = !project.img || hasImageError;

    if (shouldShowFallback) {
      return (
        <div className="flex items-center justify-center h-full w-full text-center">
          <div className="text-black font-bold text-xl md:text-2xl">{project.name}</div>
        </div>
      );
    }

    return <img src={project.img} alt={`${project.name} project screenshot`} className={`max-w-full max-h-full object-contain ${isModal ? "opacity-100" : ""}`} onError={() => handleImageError(project.id)} loading="lazy" />;
  };

  // Social links component
  const SocialLinks = () => (
    <div className="md:relative w-full md:mx-0" ref={projectRef}>
      <div className="md:absolute md:top-64 md:-left-40">
        <div className="md:rotate-[270deg]">
          <div className="md:flex flex gap-x-2 justify-center flex-row-reverse py-5 md:py-0 bg-white/70 md:bg-white/0 items-center md:flex-row-reverse md:gap-x-6">
            <a href="mailto:shaikhaltamash411@gmail.com" aria-label="Send email">
              <img src={gMail} className="h-11 w-11 p-2 bg-white rounded-[10px] md:hover:rotate-90 hover:scale-110 transition-all duration-300 ease-in-out" alt="Gmail" />
            </a>
            <a href="https://www.instagram.com/1altamas/" rel="noreferrer" target="_blank" aria-label="Visit Instagram profile">
              <img src={insta} className="h-11 w-11 p-2 bg-white rounded-[10px] md:hover:rotate-90 hover:scale-110 transition-all duration-300 ease-in-out" alt="Instagram" />
            </a>
            <a href="https://www.linkedin.com/in/1altamas" rel="noreferrer" target="_blank" aria-label="Visit LinkedIn profile">
              <img src={linkedIn} className="h-11 w-11 p-2 bg-white rounded-[10px] hover:transform md:hover:rotate-90 hover:scale-110 transition-all duration-300 ease-in-out" alt="LinkedIn" />
            </a>
            <a href="https://github.com/creotove" rel="noreferrer" target="_blank" aria-label="Visit LinkedIn profile">
              <img src={github} className="h-11 w-11 p-2 bg-white rounded-[10px] hover:transform md:hover:rotate-90 hover:scale-110 transition-all duration-300 ease-in-out" alt="LinkedIn" />
            </a>
            <p className="text-2xl px-3 py-2 font-semibold w-fit bg-x-cyan rounded-full border border-black">Social Links</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section className={`${projectClick ? "animate-wiggle" : ""} bg-vector-img`}>
      <SocialLinks />

      {/* Title */}
      <div className="flex justify-center my-10">
        <p className="text-2xl px-10 py-1 font-semibold bg-x-cyan rounded-full w-fit uppercase shadow-x">Projects</p>
      </div>

      {/* Project Cards */}
      <div className="md:mx-24 mx-7 mb-16 overflow-hidden">
        <div ref={containerRef}>
          <Slider {...sliderSettings}>
            {projects &&
              projects.length > 0 &&
              projects.map((project, idx) => (
                <div
                  className="w-96 h-96 md:w-[350px] md:h-[350px] rounded-[50px] bg-white shadow-x my-4 cursor-pointer hover:scale-105 transition-transform duration-300"
                  key={idx}
                  onClick={(e) => openProjectModal(project, e.currentTarget)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      openProjectModal(project, e.currentTarget);
                    }
                  }}>
                  <div className={`bg-x-${project.bgColor} m-6 h-3/4 rounded-[40px] flex justify-center items-center`}>
                    <ProjectImage project={project} />
                  </div>
                  <p className="text-center font-semibold text-xl">{project.name}</p>
                </div>
              ))}
          </Slider>
        </div>
      </div>

      {/* Modal */}
      <Modal color={selectedProject && `bg-x-${selectedProject.bgColor}`} isModalOpen={isProjectModalOpen} closeModal={closeProjectModal} modalOverlayRef={projectModalOverlayRef} modalContentRef={projectModalContentRef}>
        {selectedProject && (
          <div className="flex flex-col h-full mb-40">
            <div className="flex-1 flex flex-col md:flex-row gap-4 md:gap-8 mt-8">
              {/* Project image section */}
              <div className="md:w-1/2">
                <div
                  className="p-4 md:p-8 rounded-[40px] flex justify-center items-center h-48 md:h-80"
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    boxShadow: "3px 3px 0px 1px #000000",
                  }}>
                  <ProjectImage project={selectedProject} isModal={true} />
                </div>
              </div>

              {/* Project details */}
              <div className="md:w-full flex flex-col justify-center">
                <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">{selectedProject.name}</h2>

                <div className="space-y-3 md:space-y-4 text-sm md:text-base">
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold mb-2">Description:</h3>
                    <p className="leading-relaxed">{selectedProject.description || "Project description goes here..."}</p>
                  </div>

                  {selectedProject.technologies && (
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold mb-2">Technologies:</h3>
                      <p>{selectedProject.technologies}</p>
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg md:text-xl font-semibold mb-2">Features:</h3>
                    <div className="space-y-3">
                      {selectedProject.features && typeof selectedProject.features === "object" ? (
                        Object.entries(selectedProject.features).map(([key, value], index) => (
                          <div key={index} className="border-l-4 border-gray-600 pl-4">
                            <h4 className="font-semibold text-base md:text-lg mb-1">{key}:</h4>
                            <p className="text-sm md:text-base leading-relaxed">{value}</p>
                          </div>
                        ))
                      ) : (
                        <p>• Feature details...</p>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-4 mt-8">
                    {selectedProject.liveUrl && (
                      <a href={selectedProject.liveUrl} target="_blank" rel="noopener noreferrer" className="bg-x-cyan text-x-gray-dark font-bold px-6 py-2 rounded-full hover:scale-105 transition-transform" style={{boxShadow: "3px 3px 0px 3px black"}}>
                        Live Demo
                      </a>
                    )}
                    {selectedProject.githubUrl && (
                      <a
                        href={selectedProject.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gray-800 text-white font-bold px-6 py-2 rounded-full hover:scale-105 transition-transform"
                        style={{
                          backgroundColor: "rgba(0, 0, 0, 0.1)",
                          boxShadow: "3px 3px 0px 1px #000000",
                        }}>
                        GitHub
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default Projects;
